import { createFileRoute, Link } from "@tanstack/react-router";
import { Quote, ShieldCheck, ArrowRight, Award, CheckCircle2, MapPin, Zap } from "lucide-react";
import { CALENDLY_URL } from "@/lib/site";
import { trackScheduleConsultation } from "@/lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/site/Reveal";
import { CountUp } from "@/components/site/CountUp";
import { CtaPanel } from "@/components/site/Sections";
import { PricingGrid } from "@/components/site/Pricing";
import { FullTimeVsFractional } from "@/components/site/FullTimeVsFractional";
import { LeadMagnet } from "@/components/site/LeadMagnet";
import { FounderSection } from "@/components/site/FounderSection";
import {
  services as servicesData,
  industries as industriesData,
  differentiators as differentiatorsData,
  processSteps as processStepsData,
  stats as statsData,
  testimonials as testimonialsData,
  faqs as faqsData,
} from "@/lib/finendra-data";
import { useCmsList } from "@/hooks/use-cms";

import heroDashboard from "@/assets/hero-dashboard.jpg";
import teamOffice from "@/assets/team-office.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Finendra — US Bookkeeping from $199/mo & Finance Operations" },
      {
        name: "description",
        content:
          "Dedicated US bookkeeping starting at $199/mo for businesses that don't need a full-time bookkeeper. Monthly close, QuickBooks & Xero reconciliations, and controller review.",
      },
      { property: "og:title", content: "Finendra — US Bookkeeping from $199/mo" },
      {
        property: "og:description",
        content:
          "Why hire a full-time bookkeeper for $65k/year? Dedicated, CPA-ready US bookkeeping from $199/mo in QuickBooks Online and Xero.",
      },
      { property: "og:url", content: "https://finendra.com/" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Finendra" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:title", content: "Finendra — Financial Operations. Simplified." },
      {
        name: "twitter:description",
        content:
          "Full-charge bookkeeping, monthly close by the 5th, and institutional finance operations for startups and SMBs.",
      },
      { name: "twitter:image:alt", content: "Finendra — Financial Operations. Simplified." },
      { property: "og:image", content: "https://finendra.com/og/og-default.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://finendra.com/og/og-default.png" },
    ],
    links: [{ rel: "canonical", href: "https://finendra.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqsData.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),

  component: Home,
});

const partners = ["QUICKBOOKS", "XERO", "GUSTO", "BILL.COM", "RAMP", "STRIPE"];

function Home() {
  const services = useCmsList("service", servicesData);
  const industries = useCmsList("industry", industriesData);
  const differentiators = useCmsList("differentiator", differentiatorsData);
  const processSteps = useCmsList("process", processStepsData);
  const stats = useCmsList("stat", statsData);
  const testimonials = useCmsList("testimonial", testimonialsData);
  const faqs = useCmsList("faq", faqsData);

  return (
    <>
      {/* Hero */}
      <section className="px-6 pb-20 pt-24 md:pt-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <Reveal>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-brand-accent/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-navy">
                <span className="relative flex size-2">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-brand-accent opacity-75" />
                  <span className="relative inline-flex size-2 rounded-full bg-brand-accent" />
                </span>
                US Bookkeeping starting from $199/mo • No Full-Time Hire Needed
              </div>
              <h1 className="mb-8 font-display text-5xl font-extrabold leading-[1.08] tracking-tight text-brand-navy md:text-7xl">
                Why Hire a Full-Time Bookkeeper? <br />
                <span className="text-brand-accent">Get Expert Books starting from $199/mo.</span>
              </h1>
              <p className="mb-8 max-w-xl text-xl leading-relaxed text-brand-navy/60">
                Dedicated US bookkeeping, monthly bank reconciliations, and close by the 5th for
                small businesses, solo operators, and growing teams who don't need a $65k/year in-house
                bookkeeper. Pristine, CPA-ready financials in QuickBooks Online or Xero.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  search={{ plan: "essential" }}
                  className="rounded-xl bg-brand-navy px-8 py-4 font-semibold text-background transition-[background-color,color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:bg-brand-accent hover:shadow-xl active:scale-[0.98]"
                >
                  Start from $199/mo
                </Link>
                <a
                  href="#full-time-vs-fractional"
                  className="rounded-xl border border-brand-navy/15 px-8 py-4 font-semibold text-brand-navy transition-[background-color,border-color,transform] duration-150 hover:bg-brand-surface active:scale-[0.98]"
                >
                  Compare Plans &amp; Savings
                </a>
              </div>

              {/* 3 Audience Stage Cards Above the Fold */}
              <div className="mt-10 border-t border-brand-navy/10 pt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-navy/60 mb-3">
                  Fractional finance operations tailored for:
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="rounded-2xl border border-brand-navy/10 bg-brand-surface px-4 py-2.5 shadow-xs text-xs font-bold text-brand-navy flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-brand-accent" />
                    Solo Founders &amp; Single-Member LLCs
                  </div>
                  <div className="rounded-2xl border border-brand-navy/10 bg-brand-surface px-4 py-2.5 shadow-xs text-xs font-bold text-brand-navy flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-brand-accent" />
                    Agencies &amp; Consultancies ($10K–$100K/mo)
                  </div>
                  <div className="rounded-2xl border border-brand-navy/10 bg-brand-surface px-4 py-2.5 shadow-xs text-xs font-bold text-brand-navy flex items-center gap-2">
                    <span className="size-1.5 rounded-full bg-brand-accent" />
                    Startups, SaaS &amp; eCommerce
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="relative">
              <img
                src={heroDashboard}
                alt="Financial reporting dashboard used by Finendra startup clients"
                loading="eager"
                fetchPriority="high"
                width={1024}
                height={768}
                className="w-full rounded-3xl border border-brand-navy/5 shadow-2xl shadow-brand-navy/10"
              />
              <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-brand-navy/5 bg-background p-6 shadow-xl md:block">
                <div className="text-3xl font-extrabold text-brand-navy tracking-tight">5th</div>
                <div className="text-sm font-medium text-brand-navy/75">Monthly Close by</div>
              </div>
              <div className="absolute -bottom-6 -right-6 hidden rounded-2xl border border-emerald-500/20 bg-background/95 p-4 shadow-xl backdrop-blur-xs md:block">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                  Fractional Plan
                </div>
                <div className="text-2xl font-extrabold text-brand-navy">
                  <span className="text-xs font-medium text-brand-navy/60 mr-1">From</span>
                  $199<span className="text-xs font-medium text-brand-navy/60">/mo</span>
                </div>
                <div className="text-[10px] font-semibold text-emerald-600">Save 95%+ vs in-house</div>
              </div>
              <div className="absolute -top-4 -right-4 hidden items-center gap-2.5 rounded-2xl border border-brand-navy/10 bg-background/95 px-4 py-2.5 shadow-lg backdrop-blur-xs md:flex">
                <div className="flex size-7 items-center justify-center rounded-xl bg-brand-accent/15 text-brand-accent">
                  <Award className="size-4" />
                </div>
                <div>
                  <div className="text-xs font-bold text-brand-navy leading-tight">
                    Certified ProAdvisor
                  </div>
                  <div className="text-[10px] font-medium text-brand-navy/60">
                    QuickBooks &amp; Xero
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Partner marquee */}
      <section className="overflow-hidden border-y border-brand-navy/5 bg-brand-surface/40 py-10">
        <div className="mx-auto mb-6 max-w-7xl px-6">
          <p className="text-center text-xs font-bold uppercase tracking-wider text-brand-navy/70">
            Modern accounting stack we operate in
          </p>
        </div>
        <div className="flex w-max animate-marquee gap-16 px-8 opacity-70 grayscale hover:[animation-play-state:paused] focus-within:[animation-play-state:paused] cursor-pointer">
          {[...partners, ...partners].map((p, i) => (
            <span key={`${p}-${i}`} className="text-xl font-bold tracking-tight text-brand-navy">
              {p}
            </span>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 md:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="font-display text-4xl font-extrabold text-brand-navy md:text-5xl">
                <CountUp value={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-2 text-sm text-brand-navy/70 font-medium">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Full-Time vs Fractional Comparison */}
      <div id="full-time-vs-fractional">
        <FullTimeVsFractional />
      </div>

      {/* Services */}
      <section id="services" className="px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-16">
            <h2 className="mb-4 font-display text-4xl font-bold text-brand-navy">
              Comprehensive Solutions
            </h2>
            <p className="text-brand-navy/60">
              Everything your back-office needs to run on autopilot.
            </p>
          </Reveal>

          <div className="grid gap-8 md:grid-cols-3">
            {services.slice(0, 6).map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <article className="h-full rounded-3xl border border-brand-navy/5 bg-background p-8 transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:border-brand-accent/30 hover:shadow-xl motion-reduce:transform-none">
                  <div className="mb-6 flex size-12 items-center justify-center rounded-xl bg-brand-accent/10">
                    <span className="font-display text-sm font-bold text-brand-accent">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-brand-navy">{s.title}</h3>
                  <p className="mb-6 text-sm leading-relaxed text-brand-navy/60">{s.description}</p>
                  <ul className="space-y-2 text-sm font-medium text-brand-navy/80">
                    {s.points.map((p) => (
                      <li key={p}>• {p}</li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12">
            <Link to="/services" className="font-semibold text-brand-accent hover:underline">
              See all 12 services →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section className="bg-brand-surface px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-16">
            <h2 className="font-display text-4xl font-bold text-brand-navy">
              How we onboard your finance function
            </h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-3">
            {processSteps.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.1}>
                <div className="border-t-2 border-brand-accent pt-6">
                  <span className="font-display text-sm font-bold text-brand-accent">{p.step}</span>
                  <h3 className="mt-3 text-xl font-bold text-brand-navy">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-navy/60">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="overflow-hidden bg-brand-navy px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-20 lg:grid-cols-2">
            <Reveal>
              <h2 className="mb-8 font-display text-4xl font-bold text-background">
                Tailored Expertise Across Industries
              </h2>
              <div className="grid gap-x-6 sm:grid-cols-2">
                {industries.slice(0, 8).map((ind, i) => (
                  <div
                    key={ind.name}
                    className="flex items-center gap-3 border-b border-background/10 py-3 text-background"
                  >
                    <span className="text-brand-accent">{String(i + 1).padStart(2, "0")}</span>
                    {ind.name}
                  </div>
                ))}
              </div>
              <Link
                to="/industries"
                className="mt-8 inline-block font-semibold text-brand-accent hover:underline"
              >
                All industries we serve →
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <img
                src={teamOffice}
                alt="Finendra accounting professionals reviewing client reporting"
                loading="lazy"
                width={1024}
                height={1024}
                className="aspect-square w-full rounded-3xl object-cover"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why Finendra */}
      <section id="why" className="px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-16">
            <h2 className="font-display text-4xl font-bold text-brand-navy">Why Finendra</h2>
            <p className="mt-4 max-w-2xl text-brand-navy/60">
              A dependable finance operation, built around accuracy, security, and clear
              communication.
            </p>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {differentiators.map((d, i) => (
              <Reveal key={d.title} delay={(i % 4) * 0.07}>
                <div className="h-full rounded-2xl bg-brand-surface p-6">
                  <div className="mb-4 size-2 rounded-full bg-brand-accent" />
                  <h3 className="font-bold text-brand-navy">{d.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-navy/60">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="px-6 pb-24">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-2">
          <Reveal className="rounded-3xl border border-brand-navy/5 bg-brand-surface p-10">
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Our Mission
            </h2>
            <p className="mt-6 text-2xl font-bold leading-tight text-brand-navy">
              To simplify financial operations for growing businesses through dependable accounting
              expertise, modern technology, and exceptional client service.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="rounded-3xl bg-brand-navy p-10">
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Our Vision
            </h2>
            <p className="mt-6 text-2xl font-bold leading-tight text-background">
              To become a trusted global finance operations partner, enabling businesses to scale
              confidently while we manage the numbers behind their success.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-14 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">Pricing</p>
            <h2 className="mt-4 font-display text-4xl font-bold text-brand-navy md:text-5xl">
              Outcome-based bookkeeping &amp; accounting starting from $199/mo
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-brand-navy/60">
              No hourly surprises. Fixed monthly tiers scaled to your needs: Essential bookkeeping starting from
              $199/mo for businesses that don't need a full-time bookkeeper, Growth from $499/mo, Scale
              from $1,299/mo, and Custom enterprise plans.
            </p>
          </Reveal>
          <PricingGrid />
        </div>
      </section>

      {/* Founder Testimonials & Results */}
      <section className="bg-brand-surface px-6 py-24 border-y border-brand-navy/5">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-14">
            <div className="flex items-center gap-2 text-brand-accent font-bold text-xs uppercase tracking-wider">
              <ShieldCheck className="size-4" />
              Founder Testimonials & Results
            </div>
            <h2 className="mt-2 font-display text-4xl font-bold text-brand-navy">
              What founders say
            </h2>
            <p className="mt-3 text-brand-navy/60 max-w-2xl">
              Real results from venture-backed startups and high-growth businesses running daily
              operations on Finendra.
            </p>
          </Reveal>
          <div className="grid gap-8 lg:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.author || t.name} delay={i * 0.1} className="h-full">
                <figure className="group flex h-full flex-col justify-between rounded-3xl border border-brand-navy/10 bg-background p-7 sm:p-8 shadow-sm hover:shadow-xl hover:border-brand-accent/30 transition-all duration-300">
                  <div className="space-y-5">
                    {/* Header: Avatar, Name/Role/Location, Plan Badge */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className="relative shrink-0">
                          {t.avatar ? (
                            <img
                              src={t.avatar}
                              alt={t.name}
                              className="size-[52px] rounded-2xl object-cover ring-2 ring-brand-navy/10 shadow-sm"
                              width={52}
                              height={52}
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex size-[52px] items-center justify-center rounded-2xl bg-brand-accent/10 font-bold text-brand-accent text-lg ring-2 ring-brand-navy/10">
                              {t.name ? t.name.charAt(0) : "F"}
                            </div>
                          )}
                          <div
                            className="absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-background shadow-xs"
                            title="Verified Client"
                          >
                            <CheckCircle2 className="size-3.5 stroke-[2.5]" />
                          </div>
                        </div>

                        <div className="min-w-0">
                          <h3 className="font-display font-bold text-brand-navy text-base leading-snug truncate">
                            {t.name}
                          </h3>
                          <p className="text-xs font-semibold text-brand-navy/80 truncate">
                            {t.role}
                            {t.company ? ` • ${t.company}` : ""}
                          </p>
                          {t.location && (
                            <p className="flex items-center gap-1 text-[11px] text-brand-navy/55 mt-0.5">
                              <MapPin className="size-3 shrink-0 text-brand-navy/40" />
                              <span className="truncate">{t.location}</span>
                            </p>
                          )}
                        </div>
                      </div>

                      {t.plan && (
                        <span className="shrink-0 rounded-full bg-brand-accent/10 border border-brand-accent/20 px-2.5 py-1 text-[11px] font-bold text-brand-accent whitespace-nowrap">
                          {t.plan}
                        </span>
                      )}
                    </div>

                    {/* Metric / Outcome Banner */}
                    {t.metric && (
                      <div className="rounded-xl border border-emerald-500/20 bg-emerald-50/70 dark:bg-emerald-950/20 p-3">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 dark:text-emerald-300">
                          <span className="inline-block size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          <span>{t.metric}</span>
                        </div>
                        {t.highlight && (
                          <p className="text-[11px] text-emerald-700/85 dark:text-emerald-400/85 mt-0.5 font-medium leading-snug">
                            {t.highlight}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Quote text */}
                    <blockquote className="text-brand-navy/85 text-[14px] leading-relaxed relative pt-1">
                      <Quote className="size-6 text-brand-accent/15 absolute -top-2 -left-1.5 -z-10 transform -scale-x-100" />
                      "{t.quote}"
                    </blockquote>
                  </div>

                  {/* Card Bottom: Tech Stack Pills & Stage Tag */}
                  <figcaption className="mt-6 border-t border-brand-navy/10 pt-4 space-y-3">
                    {t.stack && t.stack.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-brand-navy/45 mb-1.5">
                          Financial & Tech Stack
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {t.stack.map((tool) => (
                            <span
                              key={tool}
                              className="rounded-md bg-brand-surface border border-brand-navy/10 px-2 py-0.5 text-[11px] font-medium text-brand-navy/70"
                            >
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-1 text-[11px]">
                      {t.stage && (
                        <span className="inline-flex items-center gap-1.5 font-semibold text-brand-navy/60">
                          <span className="size-1.5 rounded-full bg-brand-navy/35" />
                          {t.stage}
                        </span>
                      )}
                      <span className="font-semibold text-emerald-600 flex items-center gap-1 ml-auto">
                        <CheckCircle2 className="size-3" />
                        Verified Client
                      </span>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Magnet */}
      <section className="px-6 py-20 bg-background">
        <LeadMagnet defaultIndustry="General Startup" />
      </section>

      {/* Founder - Who's Behind It */}
      <FounderSection />

      {/* FAQ */}
      <section className="px-6 py-24">
        <div className="mx-auto max-w-3xl">
          <Reveal className="mb-10">
            <h2 className="font-display text-4xl font-bold text-brand-navy">
              Frequently asked questions
            </h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold text-brand-navy hover:text-brand-accent">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-brand-navy/60 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CtaPanel />
    </>
  );
}
